import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { connectSse, disconnectSse, sseEmitter, type SseEventMap } from '@/shared/realtime';
import {
  useNavigationContext,
  useNavigationStore,
} from '@/shared/navigation/react/useNavigationContext';
import { toast } from '@/shared/ui';
import { useAuth } from '@/shared/auth';
import { jobDetailAgenda } from './detail/jobDetailAgenda';
import { jobsOverviewAgenda } from './overview/jobsOverviewAgenda';
import { openJobDetailWindow } from './internal/jobsWindows';
import { formatDuration } from './internal/jobsFormat';
import { openJobDetail, registerJobDetailOpener, useJobsStore } from './jobsStore';

const JOB_TOAST_DURATION_MS = 7000;

/**
 * Neviditelný runtime jobs modulu (mount v shellu vedle `WindowOnboarding`):
 * - SSE lifecycle (connect po přihlášení, refetch po reconnectu),
 * - hydratace store při načtení plochy (= F5 sémantika: jen nedokončené + chybné joby),
 * - SSE handlery → store appliery + toasty (stávající toast systém, dle schváleného zadání),
 * - registrace openeru detailu (openJobDetail volatelné mimo React),
 * - zrcadlení otevřených jobs oken do store (kompaktace dokončených mimo pozornost),
 * - sekundový ticker živých dob běhu (jen když něco běží).
 */
export function JobsRuntime() {
  const { t } = useTranslation('jobs');
  const { kernel } = useNavigationContext();
  const auth = useAuth();

  // Opener detailu pro volání mimo React (toast akce, publish flow, výsledkové akce).
  useEffect(() => {
    registerJobDetailOpener((runId) => openJobDetailWindow(kernel, runId));
    return () => registerJobDetailOpener(null);
  }, [kernel]);

  // SSE spojení + hydratace po přihlášení.
  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    connectSse();
    void useJobsStore.getState().hydrate();
    return () => disconnectSse();
  }, [auth.status]);

  // SSE handlery — per-slice ownership dle backend.12 (cleanup přes off je povinný).
  useEffect(() => {
    const onRunChanged = (payload: SseEventMap['job:run-changed']) => {
      useJobsStore.getState().applyRunChanged(payload.run);
    };
    const onStepChanged = (payload: SseEventMap['job:step-changed']) => {
      useJobsStore.getState().applyStepChanged(payload.runId, payload.revision, payload.step);
    };
    const onCompleted = (payload: SseEventMap['job:completed']) => {
      const first = useJobsStore.getState().applyCompleted(payload.runId);
      if (first && payload.succeeded) {
        toast({
          severity: 'success',
          title: t('toast.completedTitle'),
          description: t('toast.completedDescription', {
            title: payload.title,
            count: payload.totalSteps,
            duration: formatDuration(payload.durationMs),
          }),
          duration: JOB_TOAST_DURATION_MS,
          action: { label: t('toast.showDetail'), onClick: () => openJobDetail(payload.runId) },
        });
      }
    };
    const onStepFailed = (payload: SseEventMap['job:step-failed']) => {
      const first = useJobsStore.getState().applyStepFailed(payload.runId);
      if (first) {
        toast({
          severity: 'error',
          title: t('toast.failedTitle'),
          description: t('toast.failedDescription', {
            title: payload.title,
            step: payload.stepName,
          }),
          duration: JOB_TOAST_DURATION_MS,
          action: { label: t('toast.showDetail'), onClick: () => openJobDetail(payload.runId) },
        });
      }
    };
    const onConnected = (payload: SseEventMap['sse:connected']) => {
      if (payload.reconnected) void useJobsStore.getState().refetchAfterReconnect();
    };

    sseEmitter.on('job:run-changed', onRunChanged);
    sseEmitter.on('job:step-changed', onStepChanged);
    sseEmitter.on('job:completed', onCompleted);
    sseEmitter.on('job:step-failed', onStepFailed);
    sseEmitter.on('sse:connected', onConnected);
    return () => {
      sseEmitter.off('job:run-changed', onRunChanged);
      sseEmitter.off('job:step-changed', onStepChanged);
      sseEmitter.off('job:completed', onCompleted);
      sseEmitter.off('job:step-failed', onStepFailed);
      sseEmitter.off('sse:connected', onConnected);
    };
  }, [t]);

  // Zrcadlení otevřených jobs oken do store + kompaktace při zavření centrálního okna
  // (design `purge()`: dokončené běhy mimo právě sledované → kompaktní řádek).
  const layers = useNavigationStore((s) => s.navigation.layers);
  const prevCentralOpenRef = useRef(false);
  useEffect(() => {
    const store = useJobsStore.getState();
    const centralOpen = layers.some((l) => l.agendaId === jobsOverviewAgenda.id);
    const detailRunIds = layers
      .filter((l) => l.agendaId === jobDetailAgenda.id)
      .map((l) => l.openParams['runId'])
      .filter((id): id is string => typeof id === 'string');
    store.setCentralOpen(centralOpen);
    store.setOpenDetailRunIds(detailRunIds);
    if (prevCentralOpenRef.current && !centralOpen) {
      store.compactCompleted(detailRunIds);
    }
    prevCentralOpenRef.current = centralOpen;
  }, [layers]);

  // Ticker živých dob — jen když nějaký běh opravdu běží.
  useEffect(() => {
    const id = window.setInterval(() => {
      const store = useJobsStore.getState();
      const anyRunning = store.order.some(
        (runId) => store.runs[runId]?.summary.status === 'Running'
      );
      if (anyRunning) store.tick(Date.now());
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return null;
}
